import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface ContentCreatorInfo {
  user_id: string;
  added_at: string;
  added_by: string;
}

export interface RegisteredAppResponse {
  id: string;
  app_id: string;
  app_name: string;
  app_icon?: string | null;
  website_url?: string | null;
  owner_id: string;
  content_creators?: ContentCreatorInfo[];
  is_active: boolean;
  app_store_id?: string | null;
  bundle_id?: string | null;
  play_store_id?: string | null;
  platform?: string | null;
  category?: string | null;
  rating?: number | null;
  rating_count?: number | null;
  store_last_synced_at?: string | null; // date-time
  created_at: string;
  updated_at: string;
}

export interface CreateAppRequest {
  app_name: string;
  website_url?: string | null;
  app_store_id?: string | null;
  bundle_id?: string | null;
  play_store_id?: string | null;
  platform?: string | null;
  category?: string | null;
}

export interface UpdateAppRequest {
  app_name?: string | null;
  website_url?: string | null;
  app_store_id?: string | null;
  bundle_id?: string | null;
  play_store_id?: string | null;
  platform?: string | null;
  category?: string | null;
  rating?: number | null;
  rating_count?: number | null;
  store_last_synced_at?: string | null; // date-time
}

export interface AddContentCreatorRequest {
  user_id: string;
}

export interface BulkAddCreatorsRequest {
  user_ids: string[];
}

export interface BulkAddResult {
  user_id: string;
  success: boolean;
  error?: string | null;
}

export interface BulkAddCreatorsResponse {
  added: string[];
  skipped: BulkAddResult[];
  failed: BulkAddResult[];
  app: RegisteredAppResponse;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  private http = inject(HttpClient);
  /** Base URL: /api/v1/apps/ — backend expects trailing slash for list/create */
  private apiUrl = `${environment.apiUrl}/apps/`;

  /** GET /api/v1/apps/ */
  getApps(skip?: number, limit?: number, is_active?: boolean): Observable<RegisteredAppResponse[]> {
    const params: Record<string, number | boolean> = {};
    if (skip !== undefined) params['skip'] = skip;
    if (limit !== undefined) params['limit'] = limit;
    if (is_active !== undefined) params['is_active'] = is_active;
    return this.http.get<RegisteredAppResponse[]>(this.apiUrl, { params });
  }

  /** POST /api/v1/apps/ */
  createApp(payload: CreateAppRequest): Observable<RegisteredAppResponse> {
    return this.http.post<RegisteredAppResponse>(this.apiUrl, payload);
  }

  /** GET /api/v1/apps/{app_id} */
  getApp(app_id: string): Observable<RegisteredAppResponse> {
    return this.http.get<RegisteredAppResponse>(`${this.apiUrl}${app_id}`);
  }

  /** PUT /api/v1/apps/{app_id} */
  updateApp(app_id: string, payload: UpdateAppRequest): Observable<RegisteredAppResponse> {
    return this.http.put<RegisteredAppResponse>(`${this.apiUrl}${app_id}`, payload);
  }

  /** DELETE /api/v1/apps/{app_id} */
  deleteApp(app_id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${app_id}`);
  }

  /** POST /api/v1/apps/{app_id}/icon */
  uploadAppIcon(app_id: string, file: File): Observable<RegisteredAppResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<RegisteredAppResponse>(`${this.apiUrl}${app_id}/icon`, formData);
  }

  /** POST /api/v1/apps/{app_id}/creators */
  addContentCreator(app_id: string, payload: AddContentCreatorRequest): Observable<RegisteredAppResponse> {
    return this.http.post<RegisteredAppResponse>(`${this.apiUrl}${app_id}/creators`, payload);
  }

  /** GET /api/v1/apps/{app_id}/creators */
  getContentCreators(app_id: string): Observable<ContentCreatorInfo[]> {
    return this.http.get<ContentCreatorInfo[]>(`${this.apiUrl}${app_id}/creators`);
  }

  /** POST /api/v1/apps/{app_id}/creators/bulk */
  bulkAddContentCreators(app_id: string, payload: BulkAddCreatorsRequest): Observable<BulkAddCreatorsResponse> {
    return this.http.post<BulkAddCreatorsResponse>(`${this.apiUrl}${app_id}/creators/bulk`, payload);
  }

  /** DELETE /api/v1/apps/{app_id}/creators/bulk */
  bulkRemoveContentCreators(app_id: string, payload: BulkAddCreatorsRequest): Observable<unknown> {
    // Angular HttpClient delete supports body via options
    return this.http.delete(`${this.apiUrl}${app_id}/creators/bulk`, { body: payload });
  }

  /** DELETE /api/v1/apps/{app_id}/creators/{user_id} */
  removeContentCreator(app_id: string, user_id: string): Observable<RegisteredAppResponse> {
    return this.http.delete<RegisteredAppResponse>(`${this.apiUrl}${app_id}/creators/${user_id}`);
  }
}
